"use client";

import { ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ReservationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-10 border border-border bg-card">
        <p className="text-xl font-default mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="">
          Submit another registration
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="parent_name" rules={{
        required: "Parent name is required"
      }} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="text-[11px] tracking-[0.2em] uppercase font-mono text-muted-foreground">
                Parent Name
              </FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="h-12 px-0 text-lg font-default bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-ring transition-colors duration-300" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="player_name" rules={{
        required: "Player name is required"
      }} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="text-[11px] tracking-[0.2em] uppercase font-mono text-muted-foreground">
                Player Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Alex Smith" className="h-12 px-0 text-lg font-default bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-ring transition-colors duration-300" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="text-[11px] tracking-[0.2em] uppercase font-mono text-muted-foreground">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="email@example.com" className="h-12 px-0 text-lg font-default bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-ring transition-colors duration-300" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem className="space-y-3">
              <FormLabel className="text-[11px] tracking-[0.2em] uppercase font-mono text-muted-foreground">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 000-0000" className="h-12 px-0 text-lg font-default bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-ring transition-colors duration-300" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="billing_address" rules={{
      required: "Billing address is required"
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase font-mono text-muted-foreground">
              Billing Address
            </FormLabel>
            <FormControl>
              <Input placeholder="123 Hockey Lane, City, State" className="h-12 px-0 text-lg font-default bg-transparent border-0 border-b border-border focus-visible:ring-0 focus-visible:border-ring transition-colors duration-300" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="jersey_size" rules={{
      required: "Please select a jersey size"
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="text-[11px] tracking-[0.2em] uppercase font-mono text-muted-foreground">
              Jersey Size
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="h-12 w-full px-0 text-lg font-default bg-transparent border-0 border-b border-border focus:ring-0 focus-visible:ring-0 focus-visible:border-ring transition-colors duration-300">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="">
                <SelectItem value="ys">Youth Small</SelectItem>
                <SelectItem value="ym">Youth Medium</SelectItem>
                <SelectItem value="yl">Youth Large</SelectItem>
                <SelectItem value="as">Adult Small</SelectItem>
                <SelectItem value="am">Adult Medium</SelectItem>
                <SelectItem value="al">Adult Large</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <div className="p-4 border border-border bg-accent/50">
        <p className="text-sm font-default text-foreground">
          Note: All camp payments of $675 are processed via Venmo to <strong>@Eian-Keough</strong>.
        </p>
      </div>

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-base font-default tracking-wide group bg-primary text-primary-foreground hover:bg-primary/90">
        {isSubmitting ? "Processing..." : <>
            Complete Registration
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </>}
      </Button>
    </Form>;
}
export default function CampSignUpForm() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.15
  });
  const formBox = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.1
  });
  const details = [{
    label: "Camp Cost",
    value: "$675"
  }, {
    label: "Payment Method",
    value: "Venmo @Eian-Keough"
  }, {
    label: "Status",
    value: "Registration Closes June 18"
  }];
  return <section data-section-id="2875" id="camp-sign-up-form" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-44 px-6 md:px-10 lg:px-16">
      <span aria-hidden="true" className="pointer-events-none absolute -right-10 top-20 select-none text-[18rem] leading-none font-default text-foreground/[0.03]">
        ICE
      </span>

      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={intro.ref} className={`lg:col-span-5 transition-all duration-1000 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-[11px] tracking-[0.3em] uppercase font-mono text-muted-foreground mb-8">
              — Registration
            </p>

            <h2 className="font-default text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-10">
              Secure
              <span className="block">
                your <em className="italic font-light text-ring">spot.</em>
              </span>
            </h2>

            <p className="font-default text-base md:text-[17px] leading-relaxed text-foreground/75 max-w-[340px] mb-12">Join my elite training course. Fill out the details below to register for the upcoming camp June 22-26 at Haymarket Iceplex. Drop-off is 8am, Pickup at 5pm. Ensure your payment is sent via Venmo to finalize your enrollment. If payment is not sent, you are NOT registered for camp. <br /><br />8am arrival/get dressed for first ice session 8:30am-10:00am first ice session, mostly focused around edge work and agility with and without pucks<br />10:30am-11:30am off ice plyometric and isometric exercises<br />11:30am-11:50am deep stretches<br />12:00pm-1:00pm lunch: Either bring one or&nbsp; chaperoned across the street to chic fil a or subway as a group<br />1:00pm-1:30pm rest/get dressed for second ice session <br />1:30pm-3:00pm second ice session focusing on transitions either the puck, move sets, and puck control, will also delve into stick work in the second ice session over the course of the week<br />3:00pm-3:20pm snack<br />3:30pm-4:30pm balance, agility, and joint strengthening session<br />4:30pm-4:50pm deep stretches<br />5:00pm release from camp</p>

            <div className="space-y-5 mb-12 max-w-sm">
              {details.map((item, i) => <div key={item.label} data-index={i} className="grid grid-cols-2 gap-4 pb-4 border-b border-border/60">
                  <span className="text-[11px] tracking-[0.2em] uppercase font-mono text-muted-foreground self-center">
                    {item.label}
                  </span>
                  <span className="text-sm font-default text-foreground">
                    {item.value}
                  </span>
                </div>)}
            </div>

            <div className="overflow-hidden w-full max-w-[280px]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1702891/img/hockey_camp_registration_action.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1702891/img/hockey_camp_registration_action.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1702891/img/hockey_camp_registration_action.webp-wvc-sizes" alt="Hockey player in action" loading="lazy" decoding="async" className="w-full h-auto object-cover" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1702891/img/Color-logo-with-background-scaled.png" />
            </div>
          </div>

          <div ref={formBox.ref} className={`lg:col-span-7 transition-all duration-1000 delay-150 ease-out ${formBox.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="camp_signup_form_registration" formId="camp_signup_form_registration" sectionName="camp-sign-up-form" defaultValues={{
            parent_name: "",
            player_name: "",
            email: "",
            phone: "",
            billing_address: "",
            jersey_size: ""
          }} validationRules={{
            parent_name: {
              required: true
            },
            player_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            billing_address: {
              required: true
            },
            jersey_size: {
              required: true
            }
          }} submitText="Complete Registration" successMessage="Registration received! Please ensure your payment is sent to @Eian-Keough via Venmo to confirm your spot." errorMessage="Registration failed. Please check your details and try again." className="bg-card/60 p-8 md:p-12 lg:p-16 border border-border/40">
              <ReservationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}